import { useCallback, useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useTranslate } from "../hooks/LanguageProvider";
import { PropagateLoader } from "react-spinners";
import "../assets/css/connection.css";
import {
  formatAuthenticationDuration,
  parseAuthenticationDurationToSeconds,
  toTimestamp,
} from "../utilities/functions";
import { usePagePreferences } from "../hooks/usePagePreferences";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface ConnectionItem {
  idlogauth: string | number;
  nom_user: string;
  prenom_user: string;
  nbre_auth: number;
  last_auth: string;
  last_auth_duration: string;
}

type SortColumn = "user" | "nbre_auth" | "last_auth" | "last_auth_duration";

const Connections: React.FC = () => {
  const { translate } = useTranslate();
  const userId = localStorage.getItem("userid");
  const {
    preferences: listPreferences,
    setPreference: setListPreference,
    setPreferences: setListPreferences,
    loaded: listPreferencesLoaded,
  } = usePagePreferences("connection", {
    limit: 10,
    currentPage: 1,
    sortColumn: "last_auth",
    sortDirection: "desc",
  });
  const [pageCount, setPageCount] = useState(0);
  const [connection, setConnections] = useState<ConnectionItem[]>([]);
  const [currentPage, setCurrentPage] = useState(Number(listPreferences.currentPage) || 1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(Number(listPreferences.limit) || 10);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<Array<string | number>>([]);
  const [sortColumn, setSortColumn] = useState<SortColumn>((listPreferences.sortColumn as SortColumn) || "last_auth");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(listPreferences.sortDirection === "asc" ? "asc" : "desc");

  const fetchConnections = useCallback(
    async (page: number, pageLimit: number) => {
      if (!userId) return;

      setLoading(true);
      try {
        const totalResponse = await fetch(
          `${backendUrl}/api/connexion/totalpage/${userId}`,
          { mode: "cors" }
        );
        const totalPages = await totalResponse.json();
        const totalItems = Number(totalPages?.[0]?.total || 0);
        const nextPageCount = Math.ceil(totalItems / pageLimit);

        setTotal(totalItems);
        setPageCount(nextPageCount);

        if (nextPageCount > 0 && page > nextPageCount) {
          setCurrentPage(nextPageCount);
          return;
        }

        const response = await fetch(
          `${backendUrl}/api/connexion/${userId}?page=${page}&limit=${pageLimit}`,
          { mode: "cors" }
        );
        const data = await response.json();
        setConnections(Array.isArray(data) ? data : []);
        setSelectedRows([]);
      } catch (error) {
        console.error("Error fetching authentication history:", error);
        setConnections([]);
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    if (!listPreferencesLoaded) return;

    const nextLimit = Number(listPreferences.limit) || 10;
    const nextPage = Number(listPreferences.currentPage) || 1;
    const nextSortColumn = (listPreferences.sortColumn as SortColumn) || "last_auth";
    const nextSortDirection = listPreferences.sortDirection === "asc" ? "asc" : "desc";

    setLimit(nextLimit);
    setCurrentPage(nextPage);
    setSortColumn(nextSortColumn);
    setSortDirection(nextSortDirection);
    fetchConnections(nextPage, nextLimit);
  }, [fetchConnections, listPreferencesLoaded, listPreferences]);

  useEffect(() => {
    if (!listPreferencesLoaded) return;
    fetchConnections(currentPage, limit);
  }, [currentPage, fetchConnections, limit, listPreferencesLoaded]);

  const getSortValue = (item: ConnectionItem, column: SortColumn) => {
    if (column === "user") {
      return `${item.nom_user || ""} ${item.prenom_user || ""}`.toLowerCase();
    }
    if (column === "last_auth") {
      return item.last_auth ? new Date(item.last_auth).getTime() : 0;
    }
    if (column === "nbre_auth") {
      return Number(item.nbre_auth || 0);
    }
    return parseAuthenticationDurationToSeconds(item.last_auth_duration);
  };

  const sortedConnections = [...connection].sort((firstItem, secondItem) => {
    const firstValue = getSortValue(firstItem, sortColumn);
    const secondValue = getSortValue(secondItem, sortColumn);

    if (firstValue < secondValue) return sortDirection === "asc" ? -1 : 1;
    if (firstValue > secondValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const isAllSelected =
    sortedConnections.length > 0 &&
    sortedConnections.every((item) => selectedRows.includes(item.idlogauth));
  const hasPagination = pageCount > 1;
  const safeForcePage =
    pageCount > 0 ? Math.min(currentPage - 1, pageCount - 1) : 0;

  const handlePageClick = (data: { selected: number }) => {
    const nextPage = data.selected + 1;
    setCurrentPage(nextPage);
    setListPreference("currentPage", nextPage);
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextLimit = Number(event.target.value);
    setLimit(nextLimit);
    setCurrentPage(1);
    setListPreferences({ limit: nextLimit, currentPage: 1 });
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      const nextDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(nextDirection);
      setListPreferences({ sortColumn: column, sortDirection: nextDirection });
      return;
    }

    setSortColumn(column);
    setSortDirection("asc");
    setListPreferences({ sortColumn: column, sortDirection: "asc" });
  };

  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <i className="las la-sort connection-sort-icon"></i>;
    }

    return (
      <i
        className={`las ${
          sortDirection === "asc" ? "la-sort-up" : "la-sort-down"
        } connection-sort-icon active`}
      ></i>
    );
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRows([]);
      return;
    }
    setSelectedRows(sortedConnections.map((item) => item.idlogauth));
  };

  const handleSelectRow = (id: string | number) => {
    setSelectedRows((currentRows) =>
      currentRows.includes(id)
        ? currentRows.filter((rowId) => rowId !== id)
        : [...currentRows, id]
    );
  };

  const exportCsv = () => {
    const rowsToExport =
      selectedRows.length > 0
        ? sortedConnections.filter((item) => selectedRows.includes(item.idlogauth))
        : sortedConnections;

    const headers = [
      translate("Utilisateur"),
      translate("Nombre d'authentification"),
      translate("Derni\u00e8re Connection"),
      translate("Dur\u00e9e"),
    ];

    const csvRows = rowsToExport.map((item) => [
      `${item.nom_user || ""} ${item.prenom_user || ""}`.trim(),
      item.nbre_auth || 0,
      item.last_auth ? toTimestamp(item.last_auth) : "",
      formatAuthenticationDuration(item.last_auth_duration),
    ]);

    const escapeCsv = (value: string | number) =>
      `"${String(value).replace(/"/g, '""')}"`;

    const csvContent = [headers, ...csvRows]
      .map((row) => row.map(escapeCsv).join(";"))
      .join("\n");

    const blob = new Blob([`\uFEFF${csvContent}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `connections-page-${currentPage}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="connection-header">
        <h4 className="connection-title">
          <i
            className="las la-user-clock"
            data-rel="bootstrap-tooltip"
            title="history"
          ></i>
          {translate("Historique des authentifications")} ({total})
        </h4>
      </div>

      <div className="connection-toolbar">
        <div className="dataTables_length" id="DataTables_Table_0_length">
          <label className="connection-limit">
            {translate("Show")}
            <select
              name="DataTables_Table_0_length"
              aria-controls="DataTables_Table_0"
              className="custom-select custom-select-sm form-control form-control-sm"
              value={limit}
              onChange={handleSelectChange}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
            </select>
            {translate("entries")}
          </label>
        </div>

        <button
          type="button"
          className="btn btn-outline-info connection-export-btn"
          onClick={exportCsv}
          disabled={sortedConnections.length === 0}
        >
          <i className="las la-download mr-2"></i>
          {translate("Download CSV")}
          {selectedRows.length > 0 && ` (${selectedRows.length})`}
        </button>
      </div>

      <div className="connection-table-wrap">
        <Table className="connection-table">
          <thead className="bg-white text-uppercase">
            <tr className="ligth">
              <th className="connection-select-column">
                <input
                  type="checkbox"
                  className="connection-checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  aria-label={translate("Select all")}
                />
              </th>
              <th>
                <button
                  type="button"
                  className="connection-sort-button"
                  onClick={() => handleSort("user")}
                >
                  {translate("Utilisateur")}
                  {renderSortIcon("user")}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className="connection-sort-button"
                  onClick={() => handleSort("nbre_auth")}
                >
                  {translate("Nombre d'authentification")}
                  {renderSortIcon("nbre_auth")}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className="connection-sort-button"
                  onClick={() => handleSort("last_auth")}
                >
                  {translate("Derni\u00e8re Connection")}
                  {renderSortIcon("last_auth")}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className="connection-sort-button"
                  onClick={() => handleSort("last_auth_duration")}
                >
                  {translate("Dur\u00e9e")}
                  {renderSortIcon("last_auth_duration")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="light-body">
            {loading ? (
              <tr>
                <td colSpan={5}>
                  <div className="connection-loader">
                    <PropagateLoader
                      color={"#123abc"}
                      loading={loading}
                      size={20}
                    />
                  </div>
                </td>
              </tr>
            ) : sortedConnections.length > 0 ? (
              sortedConnections.map((item) => (
                <tr className={`connection-row-${item.idlogauth}`} key={item.idlogauth}>
                  <td className="connection-select-column">
                    <input
                      type="checkbox"
                      className="connection-checkbox"
                      checked={selectedRows.includes(item.idlogauth)}
                      onChange={() => handleSelectRow(item.idlogauth)}
                      aria-label={`${translate("Utilisateur")} ${item.nom_user} ${item.prenom_user}`}
                    />
                  </td>
                  <td>
                    <div className="connection-user">
                      <span className="connection-user-icon">
                        <i className="las la-user"></i>
                      </span>
                      <span>
                        {item.nom_user} {item.prenom_user}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className="connection-count-badge">
                      {item.nbre_auth || 0}
                    </span>
                  </td>
                  <td>
                    {item.last_auth
                      ? toTimestamp(item.last_auth)
                      : "-"}
                  </td>
                  <td>
                    <span className="connection-duration-badge">
                      {formatAuthenticationDuration(item.last_auth_duration)}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="connection-empty">
                  {translate("No authentication history available")}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <div className="row connection-footer">
        <div className="col-md-6 d-flex align-items-center">
          <span>
            {translate("Displaying")} {connection.length} {translate("out of")}{" "}
            {total}
            {selectedRows.length > 0 && (
              <span className="connection-selection-count">
                {" "}
                - {selectedRows.length} {translate("selected")}
              </span>
            )}
          </span>
        </div>
        <div className="col-md-6 d-flex justify-content-end">
          {hasPagination && (
            <ReactPaginate
              previousLabel={translate("previous")}
              nextLabel={translate("next")}
              breakLabel={"..."}
              pageCount={pageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              containerClassName={"pagination justify-content-center"}
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              previousClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextClassName={"page-item"}
              nextLinkClassName={"page-link"}
              breakClassName={"page-item"}
              breakLinkClassName={"page-link"}
              activeClassName={"active"}
              forcePage={safeForcePage}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Connections;
